import Link from 'next/link';
import styled from 'styled-components';

import { DESKTOP_BANNER_HEIGHT, MOBILE_BANNER_HEIGHT } from 'banner/enums/constants';
import { InstantShipIcon } from 'shared/icons/InstantShipIcon';
import { variableFontRegular, variableFontMedium } from 'styles/theme/text';
import { useStoreSelector } from 'store/hooks/storeHooks';

export const Banner = () => {
  const { title, subtitle, buttonLabel, buttonLink, icon } = useStoreSelector(
    (state) => state.cmsBanner,
  );
  const isExternalLink = /^https?:/i.test(buttonLink);
  // TODO: Remove isTemporaryStagingDeprecationBanner logic when we deprecate staging.goat.com
  const isTemporaryStagingDeprecationBanner = title?.toLowerCase()?.includes('staging.goat.com');

  const getCTA = (content) =>
    isExternalLink ? (
      <Banner.CTA data-qa="banner_cta" href={buttonLink} rel="noopener noreferrer" target="_blank">
        {content}
      </Banner.CTA>
    ) : (
      <Link href={buttonLink} passHref legacyBehavior>
        <Banner.CTA data-qa="banner_cta">{content}</Banner.CTA>
      </Link>
    );

  const BannerContent = (
    <Banner.Wrapper data-qa="banner" $isRed={isTemporaryStagingDeprecationBanner}>
      <div>
        {icon && icon === 'instant_ship' && <Banner.InstantShipIcon />}
        {title && <Banner.Title data-qa="banner_title">{title}</Banner.Title>}
        {subtitle && <Banner.Subtitle data-qa="banner_subtitle">{subtitle}</Banner.Subtitle>}
        {buttonLabel && <Banner.LinkLabel>{buttonLabel}</Banner.LinkLabel>}
      </div>
    </Banner.Wrapper>
  );

  return buttonLink ? getCTA(BannerContent) : BannerContent;
};

Banner.Wrapper = styled.div<{ $isRed: boolean }>`
  height: ${MOBILE_BANNER_HEIGHT};
  background-color: ${({ $isRed, theme }) => ($isRed ? theme.colors.red : theme.colors.black)};
  color: ${({ theme }) => theme.colors.white};
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  ${variableFontRegular({ size: '9.5px', height: '12px' })};
  border-bottom: 1px solid ${({ theme }) => theme.navigation.borderColor};
  padding: 8px 16px;
  text-decoration: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: ${DESKTOP_BANNER_HEIGHT};
    ${variableFontRegular({ size: '11.5px' })};
  }
`;

Banner.InstantShipIcon = styled(InstantShipIcon)`
  margin-right: 6px;
`;

Banner.Title = styled.span`
  ${variableFontMedium({ size: '9.5px', height: '12px' })};
  margin-right: 4px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontMedium({ size: '11.5px' })};
    margin-right: 8px;
  }
`;

Banner.Subtitle = styled.span`
  margin-right: 4px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-right: 8px;
  }
`;

Banner.LinkLabel = styled.span`
  text-decoration: underline;
`;

Banner.CTA = styled.a`
  cursor: pointer;
`;
